import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gift, Check, Sparkles, Clock, ArrowRight } from "lucide-react";
import { API, useAuth } from "@/App";

const COLORS = {
  bg: "#F4EFE6",
  card: "#FFFFFF",
  text: "#2A2A2A",
  muted: "#6B6B6B",
  green: "#2D5A3D",
  greenSoft: "#4F7F5C",
  gold: "#C8A24A",
};

export default function ClaimPage() {
  const { week } = useParams();
  const navigate = useNavigate();
  const { user, token, setShowAuthModal } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [error, setError] = useState(null);

  const weekNum = parseInt(week, 10);
  const valid = !isNaN(weekNum) && weekNum >= 0 && weekNum <= 7;

  useEffect(() => {
    if (!valid) {
      setLoadingInfo(false);
      setError("Dieser Coupon-Link sieht komisch aus. Schreib uns auf info@frankys-home.de — wir helfen sofort.");
      return;
    }
    axios
      .get(`${API}/claim-coupon/${weekNum}/info`)
      .then((res) => setInfo(res.data))
      .catch(() => setError("Konnten den Coupon gerade nicht laden. Versuch's in 1 Minute nochmal."))
      .finally(() => setLoadingInfo(false));
  }, [weekNum, valid]);

  const handleClaim = async () => {
    if (!user || !token) {
      try {
        sessionStorage.setItem("claim_pending_week", String(weekNum));
      } catch (e) {}
      setShowAuthModal(true);
      toast.info("Kurz einloggen, dann gehört der Coupon dir.");
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(
        `${API}/claim-coupon/${weekNum}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.coupon) {
        setClaimed(res.data.coupon);
        if (res.data.already_claimed) {
          toast.success("Coupon ist schon in deinen Coupons.");
        } else {
          toast.success("Geschafft! Coupon ist deiner.");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Hat gerade nicht geklappt. Versuch's nochmal.";
      toast.error(msg);
      setError(msg);
    } finally {
      setClaiming(false);
    }
  };

  // Auto-claim nach Login wenn vorher pending
  useEffect(() => {
    if (user && token && !claimed && !claiming) {
      try {
        const pending = sessionStorage.getItem("claim_pending_week");
        if (pending !== null && parseInt(pending, 10) === weekNum) {
          sessionStorage.removeItem("claim_pending_week");
          handleClaim();
        }
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  if (loadingInfo) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.bg }}
      >
        <div style={{ color: COLORS.muted }}>Coupon wird geladen...</div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={{ backgroundColor: COLORS.bg }}
      >
        <div style={{ color: COLORS.text, fontSize: 18, maxWidth: 400 }}>{error}</div>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-6 py-3 rounded-full font-bold"
          style={{ backgroundColor: COLORS.green, color: "white" }}
        >
          Zurück zur Startseite
        </button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: COLORS.bg, fontFamily: "Figtree, sans-serif" }}
    >
      {/* Minimal Header */}
      <header
        className="px-4 py-4 flex items-center justify-between"
        style={{ borderBottom: `1px solid rgba(0,0,0,0.05)` }}
      >
        <button
          onClick={() => navigate("/")}
          className="font-bold text-lg"
          style={{ color: COLORS.green, fontFamily: "Playfair Display, serif" }}
        >
          Franky's Farm
        </button>
        {!user && (
          <button
            onClick={() => setShowAuthModal(true)}
            className="text-sm font-semibold underline"
            style={{ color: COLORS.text }}
          >
            Login
          </button>
        )}
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md rounded-3xl overflow-hidden"
          style={{
            backgroundColor: COLORS.card,
            boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
            border: `1px solid rgba(0,0,0,0.05)`,
          }}
        >
          {/* Top Banner */}
          <div
            className="px-6 py-5 text-center"
            style={{
              background: `linear-gradient(135deg, ${COLORS.green} 0%, ${COLORS.greenSoft} 100%)`,
              color: "white",
            }}
          >
            {claimed ? (
              <>
                <div className="flex justify-center mb-2">
                  <Check size={32} />
                </div>
                <div className="text-sm font-semibold opacity-90">
                  Coupon eingelöst
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-2">
                  <Gift size={32} />
                </div>
                <div className="text-sm font-semibold opacity-90">
                  Dein Member-Coupon
                </div>
              </>
            )}
          </div>

          {/* Body */}
          <div className="px-6 py-7 text-center">
            <h1
              className="text-2xl font-bold mb-2"
              style={{
                color: COLORS.text,
                fontFamily: "Playfair Display, serif",
              }}
            >
              {(claimed?.title || info?.title || "").trim()}
            </h1>
            <p
              className="text-sm mb-5"
              style={{ color: COLORS.muted, lineHeight: 1.6 }}
            >
              {claimed?.description || info?.description}
            </p>

            <div
              className="rounded-2xl px-4 py-4 mb-6 inline-block"
              style={{
                backgroundColor: COLORS.bg,
                border: `1px dashed ${COLORS.gold}`,
              }}
            >
              <div className="flex items-center gap-2 justify-center">
                <Sparkles size={18} style={{ color: COLORS.gold }} />
                <span
                  className="font-bold text-base"
                  style={{ color: COLORS.green }}
                >
                  {claimed?.value || info?.value}
                </span>
              </div>
            </div>

            {claimed ? (
              <>
                <div className="mb-3">
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{ color: COLORS.muted }}
                  >
                    Code für die Kasse
                  </div>
                  <div
                    className="font-mono font-bold text-2xl tracking-wider"
                    style={{ color: COLORS.text }}
                  >
                    {claimed.code}
                  </div>
                </div>
                {claimed.expires_at && (
                  <div
                    className="flex items-center justify-center gap-1 text-xs mb-5"
                    style={{ color: COLORS.muted }}
                  >
                    <Clock size={14} />
                    Gültig bis {new Date(claimed.expires_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                )}
                <button
                  onClick={() => navigate("/my-coupons")}
                  className="w-full py-4 rounded-full font-bold flex items-center justify-center gap-2"
                  style={{ backgroundColor: COLORS.green, color: "white" }}
                >
                  Zu meinen Coupons
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="w-full mt-3 py-3 text-sm font-semibold"
                  style={{ color: COLORS.muted }}
                >
                  Zur Startseite
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="w-full py-4 rounded-full font-bold flex items-center justify-center gap-2 transition-opacity"
                  style={{
                    backgroundColor: COLORS.green,
                    color: "white",
                    opacity: claiming ? 0.6 : 1,
                  }}
                >
                  {claiming ? "Einen Moment..." : user ? "Jetzt einlösen" : "Einloggen & einlösen"}
                  {!claiming && <ArrowRight size={18} />}
                </button>
                <p
                  className="text-xs mt-4"
                  style={{ color: COLORS.muted, lineHeight: 1.5 }}
                >
                  Nach dem Einlösen ist der Coupon 3 Tage gültig.<br />
                  An der Kasse einfach den Code zeigen.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
